import React, { useEffect, useCallback } from 'react';
import RealEstatesList from '../components/realEstate/RealEstatesList';
import Footer from '../Footer';
import Loader from '../utilities/Loader';
import { useSelector, useDispatch } from 'react-redux';
import {
  getRealEstates,
  addRealEstate,
  editRealEstate,
  deleteRealEstate,
  clearRealEstatesStatus,
} from '../slices/realEstateSlice';
import { getRealEstateTypes } from '../slices/realEstateTypeSlice';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import NavigationBar from '../NavigationBar';
import { useAuth0 } from '@auth0/auth0-react';
import NotFound from '../utilities/NotFound';

export default function RealEstatesPage() {
  const { isLoading, getAccessTokenSilently, isAuthenticated } = useAuth0();
  const realEstates = useSelector((state) => state.realEstates.data);
  const realEstatesStatus = useSelector((state) => state.realEstates.status);
  const realEstatesAddStatus = useSelector(
    (state) => state.realEstates.addStatus
  );
  const realEstatesEditStatus = useSelector(
    (state) => state.realEstates.editStatus
  );
  const realEstatesDeleteStatus = useSelector(
    (state) => state.realEstates.deleteStatus
  );
  const realEstatesStatusMessage = useSelector(
    (state) => state.realEstates.statusMessage
  );
  const realEstateTypes = useSelector((state) => state.realEstateTypes.data);
  const realEstateTypesStatus = useSelector(
    (state) => state.realEstateTypes.status
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (realEstatesStatus === 'idle' || realEstateTypesStatus === 'idle') {
      dispatch(getRealEstates());
      dispatch(getRealEstateTypes());
    }

    return () => {
      dispatch(clearRealEstatesStatus());
    };
  }, [dispatch, realEstatesStatus, realEstateTypesStatus]);

  useEffect(() => {
    if (
      realEstatesAddStatus === 'succeeded' ||
      realEstatesEditStatus === 'succeeded' ||
      realEstatesDeleteStatus === 'succeeded'
    ) {
      toast.success(realEstatesStatusMessage);
    } else if (
      realEstatesAddStatus === 'failed' ||
      realEstatesEditStatus === 'failed' ||
      realEstatesDeleteStatus === 'failed'
    ) {
      toast.error(realEstatesStatusMessage);
    }
  }, [
    realEstatesAddStatus,
    realEstatesEditStatus,
    realEstatesStatusMessage,
    realEstatesDeleteStatus,
  ]);

  const handleCreate = useCallback(
    async (newRealEstate) => {
      try {
        const token = await getAccessTokenSilently({
          audience: `${process.env.REACT_APP_AUTH_AUDIENCE}`,
        });
        dispatch(addRealEstate({ newRealEstate, token }));
      } catch (error) {
        console.error('Error fetching token:', error);
      }
    },
    [dispatch, getAccessTokenSilently]
  );

  const handleEdit = useCallback(
    async (updatedRealEstate) => {
      try {
        const token = await getAccessTokenSilently({
          audience: `${process.env.REACT_APP_AUTH_AUDIENCE}`,
        });
        dispatch(
          editRealEstate({
            id: updatedRealEstate.id,
            updatedRealEstate,
            token,
          })
        );
      } catch (error) {
        console.error('Error fetching token:', error);
      }
    },
    [dispatch, getAccessTokenSilently]
  );

  const handleDelete = useCallback(
    async (realEstateId) => {
      try {
        const token = await getAccessTokenSilently({
          audience: `${process.env.REACT_APP_AUTH_AUDIENCE}`,
        });
        dispatch(deleteRealEstate({ realEstateId, token }));
      } catch (error) {
        console.error('Error fetching token:', error);
      }
    },
    [dispatch, getAccessTokenSilently]
  );

  const isLoadingFetch =
    realEstatesStatus !== 'succeeded' || realEstateTypesStatus !== 'succeeded';
  const isLoadingAdd = realEstatesAddStatus === 'loading';
  const isLoadingEdit = realEstatesEditStatus === 'loading';
  const isLoadingDelete = realEstatesDeleteStatus === 'loading';

  return (
    <>
      {isLoading ||
      isLoadingFetch ||
      isLoadingAdd ||
      isLoadingEdit ||
      isLoadingDelete ? (
        <Loader />
      ) : (
        <>
          <NavigationBar />
          {isAuthenticated === true ? (
            <>
              <RealEstatesList
                realEstates={realEstates}
                realEstateTypes={realEstateTypes}
                handleCreate={handleCreate}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
              />
              <Footer />
            </>
          ) : (
            <NotFound isDetail={false} />
          )}
        </>
      )}
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        transition={Bounce}
      />
    </>
  );
}
